import type { TreeSelectSource } from "app/pages/sales-intelligence/common-components/dropdown/TreeSelectCombobox/types";
import type {
    IndustryNodeId,
    IndustryTaxonomyIndex,
} from "app/pages/sales-intelligence/domain/industry/types/taxonomy";
import {
    collectAncestorsOfChecked,
    flattenVisibleRows,
    revealAncestors,
    toggleNode,
} from "app/pages/sales-intelligence/domain/industry/utils/taxonomy";
import type { FuzzyMatchRange } from "app/pages/sales-intelligence/services/fuzzy-search/types";

import type { IndustrySearchHit } from "../../services/search/types";

import type { IndustryChip, IndustryTreeViewModel } from "./IndustryTreeViewModel";

const EMPTY_NODE_SET: ReadonlySet<IndustryNodeId> = new Set();

type SearchDeps = {
    readonly taxonomy: IndustryTaxonomyIndex;
    /** Fuzzy hits over the taxonomy — supplied by the industry core module's filter-spec, locale bound. */
    readonly searchHits: (query: string) => readonly IndustrySearchHit[];
};

type SearchState = {
    readonly visibleNodeIds: (searchQuery: string) => ReadonlySet<IndustryNodeId> | undefined;
    readonly highlightRangesForSearch: (
        searchQuery: string,
        nodeId: IndustryNodeId,
    ) => readonly FuzzyMatchRange[] | undefined;
};

/** Shared by both factories, so the two selection modes can never drift apart on what a search reveals. */
const createSearchState = ({ taxonomy, searchHits }: SearchDeps): SearchState => {
    let cachedQuery = "";
    let cachedHitsById: ReadonlyMap<IndustryNodeId, readonly FuzzyMatchRange[]> | null = null;

    const resolveHits = (searchQuery: string): ReadonlyMap<IndustryNodeId, readonly FuzzyMatchRange[]> | null => {
        const query = searchQuery.trim();

        if (!query) {
            return null;
        }

        if (cachedQuery !== query) {
            cachedQuery = query;
            cachedHitsById = new Map(searchHits(query).map((hit) => [hit.id, hit.nameRanges]));
        }

        return cachedHitsById;
    };

    return {
        visibleNodeIds: (searchQuery) => {
            const hits = resolveHits(searchQuery);

            return hits ? revealAncestors(new Set(hits.keys()), taxonomy) : undefined;
        },
        highlightRangesForSearch: (searchQuery, nodeId) => {
            const hits = resolveHits(searchQuery);

            return hits?.get(nodeId);
        },
    };
};

type FactoryDeps = SearchDeps & {
    readonly value: readonly IndustryNodeId[];
};

export const createIndustryTreeViewModel = ({ taxonomy, value, searchHits }: FactoryDeps): IndustryTreeViewModel => {
    const checked = new Set(value);
    const ancestorsOfChecked = collectAncestorsOfChecked(value, taxonomy);
    const { visibleNodeIds, highlightRangesForSearch } = createSearchState({ taxonomy, searchHits });

    const chipItems = (): readonly IndustryChip[] =>
        value.map((id) => {
            const node = taxonomy.byId.get(id);

            // An unresolved id is never dropped — it is either a node the backend removed or a value
            // read before the taxonomy loaded, and pruning it would silently empty a saved search.
            return { id, label: node?.name ?? id, isUnresolved: !node };
        });

    const treeSource: TreeSelectSource<IndustryNodeId> = {
        visibleNodeIds,
        highlightRangesForSearch,
        rows: (expanded, visible) => flattenVisibleRows(taxonomy, { expanded, checked, ancestorsOfChecked, visible }),
        toggleChecked: (id) => toggleNode(value, id, taxonomy),
    };

    return {
        ...treeSource,
        chipItems,
        removeValue: (id) => value.filter((selectedId) => selectedId !== id),
        isEmpty: () => value.length === 0,
    };
};

type SingleSelectFactoryDeps = SearchDeps & {
    readonly value: IndustryNodeId | undefined;
};

/**
 * The single-select reading of the same tree, for surfaces whose filter holds one node.
 *
 * It needs no chips or removal: the trigger names the selection and re-picking it clears. Selecting a
 * node still *matches* its whole subtree wherever the filter is applied — that is the backend's
 * contract — but only the picked row says so, which is why both derived check states are neutralised.
 */
export const createSingleSelectIndustryTreeViewModel = ({
    taxonomy,
    value,
    searchHits,
}: SingleSelectFactoryDeps): TreeSelectSource<IndustryNodeId> => {
    // Hoisted like the multi factory's: built once per selection rather than once per expand.
    const checked = value ? new Set([value]) : EMPTY_NODE_SET;
    const { visibleNodeIds, highlightRangesForSearch } = createSearchState({ taxonomy, searchHits });

    return {
        visibleNodeIds,
        highlightRangesForSearch,
        rows: (expanded, visible) =>
            flattenVisibleRows(taxonomy, {
                expanded,
                checked,
                ancestorsOfChecked: EMPTY_NODE_SET,
                visible,
                checkedCoversDescendants: false,
            }),
        toggleChecked: (id) => (id === value ? [] : [id]),
    };
};
